import { Box, BoxProps, Flex, VStack } from '@chakra-ui/react'
import {
  defaultRangeExtractor,
  Range,
  ScrollToOptions,
  useVirtualizer,
} from '@tanstack/react-virtual'
import { Key, ReactNode, useCallback, useEffect, useRef } from 'react'
import { ScrollArea, ScrollbarOptions } from '~/components/shared/ScrollArea'
import { Size } from '~/types/ThemeTypes'

import { useDebouncedCallback } from '@mangovoice/core'

import { LoadingSpinner } from './LoadingSpinner'

const END_REACHED_COOLDOWN_MS = 100
const SCROLLING_LIST_SELECTOR = '[data-testid="scrolling-list"]'
const FOCUSABLE_ROW_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Returns the focusable control of the row at `index` in the first
 * `ScrollingList` inside `root`, e.g. to move focus to the neighbour of a
 * row that is about to be removed.
 */
export const getScrollingListRowFocusTarget = (
  root: ParentNode,
  index: number,
): HTMLElement | null => {
  if (index < 0) return null
  const row = root
    .querySelector(SCROLLING_LIST_SELECTOR)
    ?.querySelector(`[data-index="${index}"]`)
  return row?.querySelector<HTMLElement>(FOCUSABLE_ROW_SELECTOR) ?? null
}

/**
 * Focus target for after the row at `removedIndex` is deleted: the next row,
 * else the previous one. Call it before the row leaves the list.
 */
export const getNeighborRowFocusTarget = (
  root: ParentNode,
  removedIndex: number,
): HTMLElement | null =>
  removedIndex < 0
    ? null
    : (getScrollingListRowFocusTarget(root, removedIndex + 1) ??
      getScrollingListRowFocusTarget(root, removedIndex - 1))

export interface ScrollToIndexParams {
  index: number
  options?: ScrollToOptions
}

type ScrollingListProps<TItemData> = Omit<BoxProps, 'direction'> & {
  itemData: TItemData[]
  selectedItemIndex?: number
  scrollToIndex?: ScrollToIndexParams
  loadThresholdPercentage?: number
  onBuildItemContent: (
    itemIndex: number,
    data: TItemData,
    isSelected: boolean,
  ) => ReactNode
  onEndReached?: () => void
  scrollbarOptions?: ScrollbarOptions
  showLoaderFooter?: boolean
  itemSpacing?: string
  /** Stable key per item (e.g. its id) so a focused row survives list changes */
  getItemKey?: (item: TItemData, index: number) => Key
  sx?: Record<string, any>
  css?: Record<string, any>
}

const LoaderFooter = () => {
  return (
    <Flex justifyContent="center" alignItems="center" height="32px">
      <LoadingSpinner size={Size.sm} />
    </Flex>
  )
}

export const ScrollingList = <TItemData,>(
  props: ScrollingListProps<TItemData>,
) => {
  const {
    itemData,
    selectedItemIndex,
    scrollToIndex,
    loadThresholdPercentage = 0.1,
    onBuildItemContent,
    onEndReached,
    scrollbarOptions = { hidden: false, showOnHoverOnly: false },
    showLoaderFooter = true,
    itemSpacing,
    getItemKey,
    ...rest
  } = props

  const handleEndReached = useDebouncedCallback(
    () => {
      if (!onEndReached) {
        return
      }

      onEndReached()
    },
    END_REACHED_COOLDOWN_MS,
    { leading: true, trailing: false },
  )

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const isMountedRef = useRef(true)

  // Keeps the row holding focus mounted when it scrolls out of view, so focus
  // isn't dropped to <body> by virtualization.
  const rangeExtractor = useCallback((range: Range) => {
    const indexes = defaultRangeExtractor(range)
    const activeElement = document.activeElement
    if (!activeElement || !scrollAreaRef.current?.contains(activeElement)) {
      return indexes
    }
    const focusedIndex = Number(
      activeElement.closest('[data-index]')?.getAttribute('data-index'),
    )
    if (
      !Number.isInteger(focusedIndex) ||
      focusedIndex >= range.count ||
      indexes.includes(focusedIndex)
    ) {
      return indexes
    }
    return [...indexes, focusedIndex].sort((a, b) => a - b)
  }, [])

  // Callers often pass an inline getItemKey; reading it from a ref keeps the
  // virtualizer's key function stable across renders. It still changes with
  // itemData, because the virtualizer only recomputes keys when this function
  // or the count changes, and a same-length reorder must re-key the rows.
  const getItemKeyRef = useRef(getItemKey)
  getItemKeyRef.current = getItemKey
  const getVirtualItemKey = useCallback(
    (index: number) =>
      getItemKeyRef.current?.(itemData[index] as TItemData, index) ?? index,
    [itemData],
  )

  const virtualizer = useVirtualizer({
    count: itemData.length,
    estimateSize: () => 100,
    getScrollElement: () => scrollAreaRef.current,
    rangeExtractor,
    getItemKey: getItemKey ? getVirtualItemKey : undefined,
  })

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const virtualSize = virtualizer.getTotalSize()
  const virtualItems = virtualizer.getVirtualItems()
  const items = virtualItems
    .sort((a, b) => a.index - b.index)
    .map((virtualItem) => {
      const item = itemData[virtualItem.index]
      const start = virtualItem.start
      const isSelected = selectedItemIndex === virtualItem.index
      return (
        <Box
          key={virtualItem.key}
          data-index={virtualItem.index}
          position={'absolute'}
          transform={`translateY(${start}px)`}
          ref={virtualizer.measureElement}
          width="100%"
          height="fit-content"
          className="mango-list-item"
        >
          {onBuildItemContent(virtualItem.index, item as TItemData, isSelected)}
        </Box>
      )
    })

  useEffect(() => {
    const currentVirtualItems = virtualizer.getVirtualItems()
    const lastItem = currentVirtualItems.reverse()[0]
    if (!lastItem) {
      return
    }

    const loadThreshold = itemData.length * loadThresholdPercentage
    const isNearEnd = lastItem.index >= itemData.length - loadThreshold
    if (isNearEnd) {
      handleEndReached()
    }
  }, [virtualItems.length, itemData.length])

  useEffect(() => {
    if (!scrollToIndex || scrollToIndex.index < 0) {
      return
    }

    // Ensure the scroll element exists and component is mounted before scrolling
    if (!scrollAreaRef.current || !isMountedRef.current) {
      return
    }

    // Use requestAnimationFrame to ensure the DOM is ready
    const rafId = requestAnimationFrame(() => {
      if (!scrollAreaRef.current || !isMountedRef.current) {
        return
      }
      try {
        virtualizer.scrollToIndex(scrollToIndex.index, scrollToIndex.options)
      } catch (error) {
        // Silently handle errors if component unmounts during scroll
        if (isMountedRef.current) {
          console.error('Error scrolling to index:', error)
        }
      }
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [scrollToIndex, virtualizer])

  return (
    <Flex overflow="auto" bg="bg.default" {...rest}>
      <Flex zIndex={0} width="100%" pr="0.25rem">
        <ScrollArea
          data-testid="scrolling-list"
          flex="1"
          width="100%"
          ref={scrollAreaRef}
          overflow="auto"
          position="relative"
          scrollbarOptions={scrollbarOptions}
        >
          <VStack align="stretch" gap=".25rem" width="100%">
            <Box
              height={`${virtualSize}px`}
              css={{
                '*.mango-list-item+*.mango-list-item': { mt: itemSpacing },
              }}
            >
              {items}
            </Box>
            {showLoaderFooter && <LoaderFooter />}
          </VStack>
        </ScrollArea>
      </Flex>
    </Flex>
  )
}
